import { useEffect, useMemo, useState } from "react";
import api, { errMsg, formatTL, formatNumber, API } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import KPICard from "@/components/app/KPICard";
import {
  Building2, AlertTriangle, CalendarClock, Mail, FileText, Search, Plus, X,
  Pencil, Trash2, Upload, ExternalLink, Sparkles, Loader2, GraduationCap, ChevronLeft, ChevronRight, MapPin, Send,
} from "lucide-react";
import { SendMailModal, MailTemplatesModal, SentEmailsSection } from "@/components/app/TenderMail";
import { BulkMailModal } from "@/components/app/BulkMail";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const MONTHS = ["OCAK","ŞUBAT","MART","NİSAN","MAYIS","HAZİRAN","TEMMUZ","AĞUSTOS","EYLÜL","EKİM","KASIM","ARALIK"];
const PAGE_SIZE = 50;

// Bölge adı normalizasyonu (Türkçe karakter + nokta/boşluk temizliği)
const TR_FOLD_MAP = { "ı":"i","ş":"s","ç":"c","ğ":"g","ü":"u","ö":"o","â":"a","î":"i","û":"u" };
const foldRegion = (s) => {
  let t = (s || "").trim().replace(/\.+$/, "").trim();
  if (!t) return "";
  t = t.replace(/İ/g, "i").replace(/I/g, "i").toLowerCase().replace(/\u0307/g, "");
  return t.split("").map((ch) => TR_FOLD_MAP[ch] || ch).join("").replace(/\s+/g, " ");
};

const emptyForm = () => ({
  name: "", title: "", phone: "", phone2: "", contact_person: "", email: "", address: "",
  district: "", city: "", tax_office: "", tax_no: "",
  tender_period: "", tender_date: "", category: "FIRMA", contact_date: "", notes: "", notes2: "",
});

function UrgencyBadge({ item }) {
  if (item.urgency === "critical") {
    const txt = item.days_left != null ? `${item.days_left} GÜN KALDI` : "BU AY İHALE";
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-[#DC2626] text-white" data-testid="urgency-critical">
      <AlertTriangle className="w-3 h-3" /> {txt}
    </span>;
  }
  if (item.urgency === "upcoming") {
    const txt = item.days_left != null ? `${item.days_left} GÜN KALDI` : "GELECEK AY";
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-[#F59E0B] text-white" data-testid="urgency-upcoming">
      <CalendarClock className="w-3 h-3" /> {txt}
    </span>;
  }
  if (item.urgency === "overdue") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-neutral-400 text-white">TARİHİ GEÇTİ</span>;
  }
  return null;
}

export default function TenderTrackingPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [urgFilter, setUrgFilter] = useState("all");
  const [missingFilter, setMissingFilter] = useState("all"); // all | noemail | noaddress
  const [regionFilter, setRegionFilter] = useState("all");
  const [showRegions, setShowRegions] = useState(false);
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [mailFirm, setMailFirm] = useState(null);     // mail gönderme penceresi
  const [showTemplates, setShowTemplates] = useState(false);
  const [showBulkMail, setShowBulkMail] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [importing, setImporting] = useState(false);
  const [analyzing, setAnalyzing] = useState(null); // file_id being analyzed

  const load = async () => {
    try {
      const { data: d } = await api.get("/tender-tracking");
      setData(d);
      if (detail) {
        const fresh = d.items.find((x) => x.id === detail.id);
        if (fresh) setDetail(fresh);
      }
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const regions = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const c of data.items) {
      const src = c.district || c.address;
      const key = foldRegion(src) || "__none__";
      const label = (src || "").trim().replace(/\.+$/, "").trim().toUpperCase() || "BELİRTİLMEMİŞ";
      const e = map.get(key);
      if (e) e.count += 1;
      else map.set(key, { key, label, count: 1 });
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase().trim();
    return data.items.filter((c) => {
      if (catFilter !== "all" && (c.category || "FIRMA") !== catFilter) return false;
      if (urgFilter === "critical" && c.urgency !== "critical") return false;
      if (urgFilter === "upcoming" && c.urgency !== "upcoming") return false;
      if (missingFilter === "noemail" && (c.email || "").trim()) return false;
      if (missingFilter === "noaddress" && ((c.address || "").trim() || (c.district || "").trim())) return false;
      if (monthFilter !== "all" && c.tender_month !== Number(monthFilter)) return false;
      if (regionFilter !== "all" && (foldRegion(c.district || c.address) || "__none__") !== regionFilter) return false;
      if (!q) return true;
      return `${c.name} ${c.title || ""} ${c.contact_person} ${c.email} ${c.phone} ${c.phone2 || ""} ${c.address} ${c.district || ""} ${c.city || ""} ${c.notes}`.toLowerCase().includes(q);
    });
  }, [data, search, monthFilter, catFilter, urgFilter, regionFilter, missingFilter]);

  useEffect(() => { setPage(1); }, [search, monthFilter, catFilter, urgFilter, regionFilter, missingFilter]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const criticalItems = useMemo(() => (data?.items || []).filter((x) => x.urgency === "critical"), [data]);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", title: c.title || "", phone: c.phone || "", phone2: c.phone2 || "",
      contact_person: c.contact_person || "", email: c.email || "", address: c.address || "",
      district: c.district || "", city: c.city || "", tax_office: c.tax_office || "", tax_no: c.tax_no || "",
      tender_period: c.tender_period || "", tender_date: c.tender_date || "", category: c.category || "FIRMA",
      contact_date: c.contact_date || "", notes: c.notes || "", notes2: c.notes2 || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/tender-tracking/${editing.id}`, form);
      else await api.post("/tender-tracking", form);
      toast.success("Kaydedildi");
      setShowForm(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`${c.name} kaydını silmek istediğinize emin misiniz?`)) return;
    try { await api.delete(`/tender-tracking/${c.id}`); toast.success("Silindi"); if (detail?.id === c.id) setDetail(null); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const importXlsx = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: r } = await api.post("/tender-tracking/upload-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(
        `${r.inserted} yeni firma aktarıldı` +
        (r.updated ? ` · ${r.updated} mevcut firmanın eksik bilgileri tamamlandı` : "") +
        (r.skipped ? ` · ${r.skipped} değişiklik gerektirmeyen kayıt` : ""),
        { duration: 8000 }
      );
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setImporting(false); }
  };

  const uploadFile = async (item, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/tender-tracking/${item.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Teklif dosyası yüklendi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteFile = async (item, fileId) => {
    if (!window.confirm("Dosyayı silmek istiyor musunuz?")) return;
    try { await api.delete(`/tender-tracking/${item.id}/files/${fileId}`); toast.success("Dosya silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const transferOffer = async (item, f) => {
    if (!window.confirm(`"${f.original_filename}" dosyası AI ile analiz edilip Teklifler sayfasına aktarılacak. Devam edilsin mi?`)) return;
    setAnalyzing(f.file_id);
    try {
      const { data: r } = await api.post(`/tender-tracking/${item.id}/files/${f.file_id}/transfer-offer`);
      toast.success(
        <div>
          <div className="font-semibold">Teklif oluşturuldu: {r.offer_no}</div>
          <div className="text-xs mt-1">{r.line_items_count} güzergah · Aylık toplam: {formatTL(r.totals?.aylik_toplam || 0)}</div>
          <button className="text-xs underline mt-1" onClick={() => navigate("/offers")}>Teklifler sayfasına git →</button>
        </div>,
        { duration: 8000 }
      );
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setAnalyzing(null); }
  };

  const fileUrl = (itemId, fileId) => {
    const token = localStorage.getItem("token");
    return `${API}/tender-tracking/${itemId}/files/${fileId}?auth=${token || ""}`;
  };

  if (!data) return <div className="p-8 text-sm text-neutral-500">Yükleniyor...</div>;

  return (
    <>
      <PageHeader
        title="İhale Takip"
        subtitle="Firma ihale dönemleri, yetkili iletişim bilgileri ve teklif dosyası arşivi."
        testid="tender-tracking-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="bulk-mail-btn" onClick={() => setShowBulkMail(true)} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white transition-colors">
              <Send className="w-3.5 h-3.5" /> Toplu Mail
            </button>
            <button data-testid="mail-templates-btn" onClick={() => setShowTemplates(true)} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Mail className="w-3.5 h-3.5" /> Mail Taslakları
            </button>
            <label className={`flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5] cursor-pointer ${importing ? "opacity-50 pointer-events-none" : ""}`}>
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              <span data-testid="tender-tracking-xlsx-upload">{importing ? "Aktarılıyor..." : "Excel Yükle"}</span>
              <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importXlsx(f); e.target.value = ""; }} />
            </label>
            <button data-testid="tender-tracking-add-btn" onClick={openCreate} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors">
              <Plus className="w-4 h-4" strokeWidth={2.2} /> Yeni Firma
            </button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KPICard testid="kpi-tt-total" label="Toplam Firma" value={formatNumber(data.stats.total)} sub="İhale takibinde" accent="info" icon={Building2} />
        <KPICard testid="kpi-tt-critical" label="Bu Ay İhale" value={formatNumber(data.stats.critical)} sub="Acil takip gerekli" accent="error" icon={AlertTriangle} />
        <KPICard testid="kpi-tt-upcoming" label="Gelecek Ay" value={formatNumber(data.stats.upcoming)} sub="Yaklaşan ihaleler" accent="warning" icon={CalendarClock} />
        <KPICard testid="kpi-tt-email" label="Mail Adresi Olan" value={formatNumber(data.stats.with_email)} sub={`${formatNumber(data.stats.missing_email || 0)} firmada mail YOK`} accent="info" icon={Mail} />
        <KPICard testid="kpi-tt-files" label="Teklif Dosyalı" value={formatNumber(data.stats.with_files)} sub="Dosya eklenmiş" accent="success" icon={FileText} />
      </div>

      {/* Critical warning banner */}
      {criticalItems.length > 0 && (
        <div className="bg-[#FEF2F2] border border-[#FECACA] p-4 rounded-md mb-6" data-testid="critical-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-[#DC2626]" />
            <span className="text-sm font-bold text-[#DC2626]">İHALESİ YAKLAŞAN {criticalItems.length} FİRMA VAR!</span>
            <button onClick={() => setUrgFilter("critical")} className="ml-auto text-xs uppercase tracking-widest font-semibold text-[#DC2626] hover:underline">Tümünü filtrele →</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {criticalItems.slice(0, 20).map((c) => (
              <button key={c.id} onClick={() => setDetail(c)} className="px-2 py-1 bg-white border border-[#FECACA] text-xs hover:bg-[#DC2626] hover:text-white transition-colors">
                {c.name}{c.days_left != null ? ` · ${c.days_left}g` : ""}
              </button>
            ))}
            {criticalItems.length > 20 && <span className="px-2 py-1 text-xs text-[#DC2626]">+{criticalItems.length - 20} daha...</span>}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-4 rounded-md mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-neutral-400" />
          <input data-testid="tt-search" placeholder="Firma, yetkili, mail, adres ara..." value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 px-2 py-1.5 text-sm border-0 focus:outline-none" />
        </div>
        <select data-testid="tt-month-filter" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="px-2 py-1.5 text-xs border border-[#E5E5E5] uppercase tracking-wider">
          <option value="all">TÜM AYLAR</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <select data-testid="tt-region-filter" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} className="px-2 py-1.5 text-xs border border-[#E5E5E5] uppercase tracking-wider max-w-[220px]">
          <option value="all">TÜM BÖLGELER ({regions.length})</option>
          {regions.map((r) => <option key={r.key} value={r.key}>{r.label} ({r.count})</option>)}
        </select>
        <button
          data-testid="tt-regions-toggle"
          onClick={() => setShowRegions(!showRegions)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border transition-colors ${showRegions ? "bg-[#0020C2] text-white border-[#0020C2]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}
        >
          <MapPin className="w-3.5 h-3.5" /> Bölgeler
        </button>
        <div className="flex items-center gap-1">
          {[["all", "Tümü"], ["FIRMA", "Firma"], ["OKUL", "Okul"]].map(([v, l]) => (
            <button key={v} data-testid={`tt-cat-${v}`} onClick={() => setCatFilter(v)} className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border ${catFilter === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {[["all", "Hepsi"], ["critical", "🔴 Bu Ay"], ["upcoming", "🟠 Gelecek Ay"]].map(([v, l]) => (
            <button key={v} data-testid={`tt-urg-${v}`} onClick={() => setUrgFilter(v)} className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border ${urgFilter === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {[["noemail", `Mail Yok (${data.stats.missing_email || 0})`], ["noaddress", `Adres Yok (${data.stats.missing_address || 0})`]].map(([v, l]) => (
            <button
              key={v}
              data-testid={`tt-missing-${v}`}
              onClick={() => setMissingFilter(missingFilter === v ? "all" : v)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border ${missingFilter === v ? "bg-[#DC2626] text-white border-[#DC2626]" : "border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2]"}`}
            >{l}</button>
          ))}
        </div>
      </div>

      {/* Bölge dağılımı paneli */}
      {showRegions && (
        <div className="bg-white border border-[#E5E5E5] p-5 rounded-md mb-4" data-testid="tt-regions-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-500 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Bölge Dağılımı ({regions.length} bölge)
            </div>
            {regionFilter !== "all" && (
              <button data-testid="tt-region-clear" onClick={() => setRegionFilter("all")} className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-[#DC2626] hover:underline">
                <X className="w-3 h-3" /> Filtreyi Temizle
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-[300px] overflow-y-auto">
            {regions.map((r) => (
              <button
                key={r.key}
                data-testid={`tt-region-chip-${r.key.replace(/[^a-z0-9]/g, "-")}`}
                onClick={() => setRegionFilter(regionFilter === r.key ? "all" : r.key)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs border transition-colors ${regionFilter === r.key ? "bg-[#0020C2] text-white border-[#0020C2]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}
              >
                <span className="font-semibold">{r.label}</span>
                <span className={`num text-[10px] px-1.5 py-0.5 ${regionFilter === r.key ? "bg-white/20" : "bg-[#F5F5F5]"}`}>{r.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
              <tr>
                {["Firma", "Yetkili", "İletişim", "İhale Dönemi", "Not", "Dosya", ""].map((h) => (
                  <th key={h} className="px-3 py-3 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageItems.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-sm text-neutral-500">Kayıt bulunamadı. Excel yükleyerek başlayın.</td></tr>
              ) : pageItems.map((c) => (
                <tr key={c.id} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer" data-testid={`tt-row-${c.id}`} onClick={() => setDetail(c)}>
                  <td className="px-3 py-3">
                    <div className="font-semibold flex items-center gap-1.5">
                      {c.category === "OKUL" && <GraduationCap className="w-3.5 h-3.5 text-[#0020C2]" />}
                      {c.name}
                    </div>
                    {(c.address || "").trim() || (c.district || "").trim() ? (
                      <div className="text-xs text-neutral-500 max-w-[280px] truncate">
                        {[c.district, c.city].filter(Boolean).join(" / ")}
                        {c.district && c.address ? " — " : ""}{c.address}
                      </div>
                    ) : (
                      <span className="inline-block mt-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-[#DC2626] text-white" data-testid={`tt-noaddress-${c.id}`}>Adres Yok</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-neutral-600">{c.contact_person || "-"}</td>
                  <td className="px-3 py-3">
                    <div className="text-xs num">{c.phone || "-"}{c.phone2 ? ` · ${c.phone2}` : ""}</div>
                    {(c.email || "").trim() ? (
                      <div className="text-xs text-[#0020C2] truncate max-w-[220px]">{c.email}</div>
                    ) : (
                      <span className="inline-block mt-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-[#DC2626] text-white" data-testid={`tt-noemail-${c.id}`}>Mail Yok</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-1 items-start">
                      {c.tender_date ? (
                        <span className="num text-xs font-semibold">{c.tender_date}</span>
                      ) : c.tender_period ? (
                        <span className="text-xs font-semibold uppercase">{c.tender_period}</span>
                      ) : <span className="text-xs text-neutral-400">-</span>}
                      <UrgencyBadge item={c} />
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs text-neutral-500 max-w-[260px]"><div className="line-clamp-2">{c.notes || "-"}</div></td>
                  <td className="px-3 py-3">
                    {(c.files || []).length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#16A34A]"><FileText className="w-3.5 h-3.5" /> {c.files.length}</span>
                    ) : <span className="text-xs text-neutral-400">-</span>}
                  </td>
                  <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex gap-1">
                      <button
                        data-testid={`tt-mail-${c.id}`}
                        onClick={() => setMailFirm(c)}
                        disabled={!(c.email || "").trim()}
                        title={(c.email || "").trim() ? `Mail gönder (${c.email})${c.sent_emails?.length ? ` · ${c.sent_emails.length} mail gönderildi` : ""}` : "Mail adresi yok"}
                        className="relative p-1.5 hover:bg-[#0020C2] hover:text-white disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-inherit"
                      >
                        <Send className="w-3.5 h-3.5" />
                        {(c.sent_emails?.length || 0) > 0 && (
                          <span className="absolute -top-1 -right-1 bg-[#16A34A] text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center num">{c.sent_emails.length}</span>
                        )}
                      </button>
                      <button data-testid={`tt-edit-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 hover:bg-[#F5F5F5]"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`tt-delete-${c.id}`} onClick={() => remove(c)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E5E5] bg-[#FAFAFA]">
          <div className="text-xs text-neutral-500"><span className="num font-semibold">{filtered.length}</span> kayıt · Sayfa {page}/{pageCount}</div>
          <div className="flex items-center gap-1">
            <button data-testid="tt-prev-page" disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 border border-[#E5E5E5] disabled:opacity-30 hover:bg-white"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="tt-next-page" disabled={page >= pageCount} onClick={() => setPage(page + 1)} className="p-1.5 border border-[#E5E5E5] disabled:opacity-30 hover:bg-white"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {/* Create/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{editing ? "Düzenle" : "Yeni"}</div>
                <div className="font-heading font-bold text-lg">İhale Takip Firması</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Firma Adı *</label>
                <input data-testid="tt-field-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ünvan</label>
                <input data-testid="tt-field-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yetkili Kişi</label>
                <input data-testid="tt-field-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Telefon</label>
                  <input data-testid="tt-field-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Cep</label>
                  <input data-testid="tt-field-phone2" value={form.phone2} onChange={(e) => setForm({ ...form, phone2: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Mail Adresi {!form.email && <span className="text-[#DC2626] font-bold">— YOK</span>}</label>
                <input data-testid="tt-field-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`mt-1.5 w-full px-3 py-2 bg-white border text-sm ${form.email ? "border-[#E5E5E5]" : "border-[#DC2626]"}`} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Adres {!form.address && <span className="text-[#DC2626] font-bold">— YOK</span>}</label>
                <input data-testid="tt-field-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={`mt-1.5 w-full px-3 py-2 bg-white border text-sm ${form.address ? "border-[#E5E5E5]" : "border-[#DC2626]"}`} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Semt (Bölge)</label>
                  <input data-testid="tt-field-district" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Şehir</label>
                  <input data-testid="tt-field-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Vergi Dairesi</label>
                  <input data-testid="tt-field-taxoffice" value={form.tax_office} onChange={(e) => setForm({ ...form, tax_office: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Vergi No</label>
                  <input data-testid="tt-field-taxno" value={form.tax_no} onChange={(e) => setForm({ ...form, tax_no: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">İhale Dönemi (Ay)</label>
                <select data-testid="tt-field-period" value={form.tender_period} onChange={(e) => setForm({ ...form, tender_period: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                  <option value="">Belirsiz</option>
                  {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Kesin İhale Tarihi (opsiyonel)</label>
                <input data-testid="tt-field-date" type="date" value={form.tender_date} onChange={(e) => setForm({ ...form, tender_date: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Kategori</label>
                <select data-testid="tt-field-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                  <option value="FIRMA">FİRMA</option>
                  <option value="OKUL">OKUL</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Son Görüşme Tarihi</label>
                <input data-testid="tt-field-contactdate" type="date" value={form.contact_date} onChange={(e) => setForm({ ...form, contact_date: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Not</label>
                <textarea data-testid="tt-field-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Not 2</label>
                <textarea data-testid="tt-field-notes2" rows={2} value={form.notes2} onChange={(e) => setForm({ ...form, notes2: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="tt-submit-btn" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] transition-colors">{editing ? "Kaydet" : "Oluştur"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white w-full max-w-3xl rounded-md shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tt-detail-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-2">
                  Firma Detayı {detail.category === "OKUL" && <span className="px-1.5 py-0.5 bg-[#DBEAFE] text-[#0020C2] font-bold">OKUL</span>}
                </div>
                <div className="font-heading font-bold text-lg">{detail.name}</div>
              </div>
              <div className="flex items-center gap-2">
                {(detail.email || "").trim() && (
                  <button data-testid="tt-detail-mail" onClick={() => setMailFirm(detail)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A]">
                    <Send className="w-3.5 h-3.5" /> Mail Gönder
                  </button>
                )}
                <button data-testid="tt-detail-edit" onClick={() => { setDetail(null); openEdit(detail); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">
                  <Pencil className="w-3.5 h-3.5" /> Düzenle
                </button>
                <button onClick={() => setDetail(null)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  ["Ünvan", detail.title],
                  ["Yetkili", detail.contact_person],
                  ["Telefon", [detail.phone, detail.phone2].filter(Boolean).join(" · ")],
                  ["Mail", detail.email, !((detail.email || "").trim())],
                  ["Adres", detail.address, !((detail.address || "").trim())],
                  ["Semt / Şehir", [detail.district, detail.city].filter(Boolean).join(" / ")],
                  ["Vergi D. / No", [detail.tax_office, detail.tax_no].filter(Boolean).join(" / ")],
                  ["İhale Dönemi", detail.tender_date || detail.tender_period],
                  ["Son Görüşme", detail.contact_date],
                ].map(([l, v, missing]) => (
                  <div key={l}>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">{l}</div>
                    {missing ? (
                      <span className="inline-block mt-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-[#DC2626] text-white">{l} Yok</span>
                    ) : (
                      <div className="text-sm font-semibold mt-0.5 break-words">{v || "-"}</div>
                    )}
                  </div>
                ))}
              </div>
              <div><UrgencyBadge item={detail} /></div>
              <SentEmailsSection firm={detail} onChanged={load} />
              {(detail.notes || detail.notes2) && (
                <div className="bg-[#FAFAFA] border border-[#F5F5F5] p-3 text-xs text-neutral-600 space-y-1">
                  {detail.notes && <div>{detail.notes}</div>}
                  {detail.notes2 && <div className="border-t border-[#E5E5E5] pt-1">{detail.notes2}</div>}
                </div>
              )}

              {/* Files */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-500">Teklif Dosyaları ({(detail.files || []).length})</div>
                  <label className="cursor-pointer text-[11px] uppercase tracking-widest font-semibold text-[#0020C2] hover:underline flex items-center gap-1">
                    <Upload className="w-3.5 h-3.5" />
                    <span data-testid="tt-file-upload">PDF / Excel Yükle</span>
                    <input type="file" accept=".pdf,.xlsx,.xls" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(detail, f); e.target.value = ""; }} />
                  </label>
                </div>
                {(detail.files || []).length === 0 ? (
                  <div className="text-xs text-neutral-400 border border-dashed border-[#E5E5E5] p-6 text-center">Henüz teklif dosyası eklenmemiş. PDF veya Excel yükleyin.</div>
                ) : (
                  <div className="space-y-1.5">
                    {detail.files.map((f) => (
                      <div key={f.file_id} className="flex items-center justify-between bg-[#FAFAFA] px-3 py-2.5 text-xs border border-[#F5F5F5]" data-testid={`tt-file-${f.file_id}`}>
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FileText className="w-4 h-4 text-neutral-500 shrink-0" />
                          <div className="min-w-0">
                            <div className="truncate font-semibold">{f.original_filename}</div>
                            {f.transferred_offer_no && (
                              <button onClick={() => navigate("/offers")} className="text-[10px] text-[#16A34A] font-bold hover:underline">✓ Teklife aktarıldı: {f.transferred_offer_no}</button>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            data-testid={`tt-transfer-${f.file_id}`}
                            disabled={analyzing === f.file_id}
                            onClick={() => transferOffer(detail, f)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50 transition-colors"
                          >
                            {analyzing === f.file_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                            {analyzing === f.file_id ? "Analiz..." : f.transferred_offer_no ? "Tekrar Aktar" : "Teklife Aktar (AI)"}
                          </button>
                          <a href={fileUrl(detail.id, f.file_id)} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-white border border-transparent hover:border-[#E5E5E5]"><ExternalLink className="w-3.5 h-3.5" /></a>
                          <button onClick={() => deleteFile(detail, f.file_id)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mail gönderme + taslak yönetimi */}
      {mailFirm && (
        <SendMailModal firm={mailFirm} onClose={() => setMailFirm(null)} onSent={load} />
      )}
      {showTemplates && <MailTemplatesModal onClose={() => setShowTemplates(false)} />}
      {showBulkMail && <BulkMailModal firms={data.items} onClose={() => setShowBulkMail(false)} onDone={load} />}
    </>
  );
}
